import api, { setTokens, clearTokens } from './api';
import { withIdempotencyKey } from './utils/idempotency';
import { withAvatarCacheBust } from './utils/avatarCacheBust';

// Auth API
export async function login(username: string, password: string) {
    const response = await api.post('/auth/login', { username, password });
    const { access, id, refresh } = response.data;
    setTokens({ access, id, refresh });
    return { access, id, refresh };
}

export function logout() {
    clearTokens();
}

// Fetch paginated events with filters
export async function fetchEvents({ page = 0, limit = 8, category, status, search, price, date, city }: { page?: number, limit?: number, category?: string, status?: string, search?: string, price?: string, date?: string, city?: string }) {
    const params: any = { page, limit };
    if (category) params.category = category;
    if (status) params.status = status;
    if (search) params.search = search;
    if (price && price !== 'all') params.priceType = price;
    if (date) params.date = date;
    if (city) params.city = city;
    const res = await api.get('/eventopia/events', { params });
    return res.data;
}

export async function fetchActiveCategories(): Promise<string[]> {
    const res = await api.get('/eventopia/events/categories');
    return res.data.data ?? [];
}

// Fetch cities from events
export async function fetchCities() {
    const res = await api.get('/eventopia/events/cities');
    return res.data.data; // Access the data property from the response
}

// Fetch event by ID
export async function fetchEventById(id: string, sgd?: string) {
    const params: any = {};
    if (sgd) params.sgd = sgd;
    const res = await api.get(`/eventopia/events/${id}`, { params });
    return res.data;
}

// Fetch event by handle
export async function fetchEventByHandle(handle: string, sgd?: string) {
    const params: any = {};
    if (sgd) params.sgd = sgd;
    const res = await api.get(`/eventopia/events/handle/${handle}`, { params });
    return res.data;
}

// Check handle availability
export async function checkHandleAvailability(handle: string, excludeEventId?: string) {
    const params: any = {};
    if (excludeEventId) params.excludeEventId = excludeEventId;
    const res = await api.get(`/eventopia/events/handle/${handle}/check-availability`, { params });
    return res.data;
}

export async function fetchOrganizerEventById(id: string) {
    const res = await api.get(`/eventopia/events/${id}/organizer`);
    return res.data;
}

export async function fetchDraftById(id: string) {
    const res = await api.get(`/eventopia/events/drafts/${id}`);
    return res.data;
}

export async function fetchEventOrdersById(id: string) {
    const res = await api.get(`/eventopia/orders/event/${id}`);
    return res.data;
}

// Advanced search for events
export async function searchEvents({ page = 0, limit = 8, category, status, search, organizerId, startDate, endDate, location, sortBy, sortOrder }: { page?: number, limit?: number, category?: string, status?: string, search?: string, organizerId?: string, startDate?: string, endDate?: string, location?: string, sortBy?: string, sortOrder?: 'asc' | 'desc' }) {
    const params: any = { page, limit, category, status, search, organizerId, startDate, endDate, location, sortBy, sortOrder };
    const res = await api.get('/eventopia/search/events', { params });
    return res.data;
}

// =====================
// Events API
// =====================
export async function createEvent(eventData: any) {
    const res = await api.post('/eventopia/events', eventData);
    return res.data;
}

export async function createEventWithImages(formData: FormData, eventId?: string) {
    const url = eventId
        ? `/eventopia/events/with-images?eventId=${eventId}`
        : '/eventopia/events/with-images';
    const res = await api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
}

export async function saveDraft(eventData: any, eventId?: string) {
    const url = eventId
        ? `/eventopia/events/draft?eventId=${eventId}`
        : '/eventopia/events/draft';

    // Check if eventData is FormData (has files) or regular object
    if (eventData instanceof FormData) {
        const res = await api.post(url, eventData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return res.data;
    } else {
        const res = await api.post(url, eventData);
        return res.data;
    }
}

export async function getMyDrafts() {
    const res = await api.get('/eventopia/events/drafts/my');
    return res.data;
}

export async function updateEvent(id: string, eventData: any) {
    const res = await api.put(`/eventopia/events/${id}`, eventData);
    return res.data;
}

export async function deleteEvent(id: string) {
    const res = await api.delete(`/eventopia/events/${id}`);
    return res.data;
}

export async function likeEvent(id: string) {
    const res = await api.post(`/eventopia/events/${id}/like`);
    return res.data;
}

// =====================
// Event Likes API
// =====================

// Toggle event like (like/unlike)
export async function toggleEventLike(eventId: string) {
    const res = await api.post(`/eventopia/events/likes/${eventId}/toggle`);
    return res.data;
}

// Get event like statistics
export async function getEventLikeStats(eventId: string) {
    const res = await api.get(`/eventopia/events/likes/${eventId}/stats`);
    return res.data;
}

// Get user's liked events with pagination
export async function getUserLikedEvents({ page = 1, limit = 10 }: { page?: number, limit?: number } = {}) {
    const params: any = { page, limit };
    const res = await api.get('/eventopia/events/likes/user/liked-events', { params });
    return res.data;
}

// Get users who liked an event with pagination
export async function getUsersWhoLikedEvent(eventId: string, { page = 1, limit = 10 }: { page?: number, limit?: number } = {}) {
    const params: any = { page, limit };
    const res = await api.get(`/eventopia/events/likes/${eventId}/liked-users`, { params });
    return res.data;
}

// Unlike an event
export async function unlikeEvent(eventId: string) {
    const res = await api.delete(`/eventopia/events/likes/${eventId}`);
    return res.data;
}

export async function fetchEventsByOrganizer(id: string) {
    const res = await api.get(`/eventopia/events/organizer/${id}`);
    return res.data;
}

export async function fetchEventsByUser(id: string) {
    const res = await api.get(`/eventopia/events/user/${id}`);
    return res.data;
}

export async function fetchOrganizerOwnEvents(id: string) {
    const res = await api.get(`/eventopia/events/organizer/${id}/own`);
    return res.data;
}

export async function fetchOrganizerOwnEventsPublic(id: string) {
    const res = await api.get(`/eventopia/events/organizer/${id}/own/public`);
    return res.data;
}

// =====================
// Tickets API
// =====================
// Deprecated: Tickets are now included in event data, no separate endpoint needed
// Use event.tickets from fetchEventById or fetchOrganizerEventById instead
export async function fetchTicketsForEvent(eventId: string) {
    // This endpoint is deprecated - tickets are now included in event data
    // Keeping for backward compatibility but should not be used
    const res = await api.get(`/eventopia/tickets/events/${eventId}`);
    return res.data;
}

export async function createTicket(eventId: string, ticketData: any) {
    const res = await api.post(`/eventopia/tickets/events/${eventId}`, ticketData);
    return res.data;
}

export async function updateTicket(id: string, ticketData: any) {
    const res = await api.put(`/eventopia/tickets/${id}`, ticketData);
    return res.data;
}

export async function deleteTicket(id: string) {
    const res = await api.delete(`/eventopia/tickets/${id}`);
    return res.data;
}

export async function duplicateTicket(id: string) {
    const res = await api.post(`/eventopia/tickets/${id}/duplicate`);
    return res.data;
}

export async function fetchTicketById(id: string) {
    const res = await api.get(`/eventopia/tickets/${id}`);
    return res.data;
}

export async function fetchUserTicket() {
    const res = await api.get(`/eventopia/tickets/user`);
    return res.data;
}

// =====================
// Orders API
// =====================
export async function fetchOrders({ page = 1, limit = 10, type = 'upcoming' }: { page?: number, limit?: number, type?: 'upcoming' | 'past' } = {}) {
    const params: any = { page, limit, type };
    const res = await api.get('/eventopia/orders', { params });
    return res.data;
}

export async function fetchOrganizerTransactions({ page = 1, limit = 50 }: { page?: number, limit?: number } = {}) {
    const params: any = { page, limit };
    const res = await api.get('/eventopia/payments/organizer/transactions', { params });
    return res.data;
}

export async function createOrder(orderData: any) {
    const res = await api.post('/eventopia/orders', withIdempotencyKey(orderData));
    return res.data;
}

// Guest: Create a guest order
export async function createGuestOrder(orderData: { guestCartId: string; guestEmail: string; guestPhone: string;[key: string]: any }) {
    const res = await api.post('/eventopia/orders/guest', withIdempotencyKey(orderData));
    return res.data;
}

export async function createBulkFreeOrders(payload: {
    eventId: string;
    ticketId: string;
    rows: Array<{ name: string; email: string; phone?: string; qty?: number }>;
    selectedDate?: string;
    promoCode?: string;
}): Promise<{ success: boolean; data: { queued: number; failed: Array<{ email: string; reason: string }> } }> {
    const res = await api.post('/eventopia/orders/bulk-free', payload);
    return res.data;
}

export async function fetchOrderById(id: string) {
    const res = await api.get(`/eventopia/orders/${id}`);
    return res.data;
}

export async function updateOrderStatus(id: string, statusData: any) {
    const res = await api.put(`/eventopia/orders/${id}/status`, statusData);
    return res.data;
}

export async function fetchOrderQr(id: string) {
    const res = await api.get(`/eventopia/orders/${id}/qr`);
    return res.data;
}

export async function fetchOrderByHash(id: string, lastName = '') {
    const res = await api.get(`/eventopia/orders/search/with-tickets`, { params: { identifier: id, lastName: lastName } });
    return res.data;
}

export async function fetchOrderReceiptInfo(id: string, lastName = '') {
    const res = await api.get(`/eventopia/orders/search/receipt-info`, { params: { identifier: id, lastName: lastName } });
    return res.data;
}

// =====================
// Order Management API (Check-in, Walk-in, Refunds)
// =====================

// Check-in ticket by QR code
export async function checkinTicket(eventId: string, hashedUniqueId: string, qrCode: string, checkedInType: string = 'qr') {
    const res = await api.post('/eventopia/orders/checkin', {
        eventId,
        hashedUniqueId,
        qrCode,
        checkedInType
    });
    return res.data;
}

// Create walk-in order
export async function createWalkinOrder(orderData: {
    eventId: string;
    tickets: Array<{
        ticketId: string;
        quantity: number;
        selectedDate?: string;
    }>;
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    paymentNotes?: string;
}) {
    const res = await api.post('/eventopia/orders/walkin', withIdempotencyKey(orderData));
    return res.data;
}

// Refund order
export async function refundOrder(orderId: string, refundData: {
    reason: string;
    refundAmount?: number;
    refundNotes?: string;
}) {
    const res = await api.post(`/eventopia/orders/${orderId}/refund`, refundData);
    return res.data;
}

// =====================
// Payments API
// =====================
export async function fetchPayments({ page = 1, limit = 10 }: { page?: number, limit?: number } = {}) {
    const params: any = { page, limit };
    const res = await api.get('/eventopia/payments', { params });
    return res.data;
}

export async function createPayment(paymentData: any) {
    const res = await api.post('/eventopia/payments', paymentData);
    return res.data;
}

export async function fetchPaymentById(id: string) {
    const res = await api.get(`/eventopia/payments/${id}`);
    return res.data;
}

export async function refundPayment(id: string, refundData: any) {
    const res = await api.post(`/eventopia/payments/${id}/refund`, refundData);
    return res.data;
}

// =====================
// Payouts API
// =====================
export async function createConnectAccount(accountData: any) {
    const res = await api.post('/eventopia/payouts/connect/account', accountData);
    return res.data;
}

export async function createAccountLink(data: any) {
    const res = await api.post('/eventopia/payouts/connect/account-link', data);
    return res.data;
}

export async function createAccountLinkForCurrentUser(data: any) {
    const res = await api.post('/eventopia/payouts/connect/account-link/current', data);
    return res.data;
}

export async function getPayoutAccount() {
    const res = await api.get('/eventopia/payouts/account');
    return res.data;
}

export async function updatePayoutAccount(updateData: any) {
    const res = await api.put('/eventopia/payouts/account', updateData);
    return res.data;
}

export async function refreshPayoutAccount(data: {
    email: string;
    type: 'express' | 'standard' | 'custom';
    country: string;
    businessType?: 'individual' | 'company';
    companyName?: string;
    phone?: string;
}) {
    const res = await api.post('/eventopia/payouts/account/refresh', data);
    return res.data;
}



export async function requestPayout(payoutData: any) {
    const res = await api.post('/eventopia/payouts/request', payoutData);
    return res.data;
}

export async function approvePayout(payoutId: string) {
    const res = await api.post(`/eventopia/payouts/${payoutId}/approve`);
    return res.data;
}

export async function rejectPayout(payoutId: string, rejectionReason: string) {
    const res = await api.post(`/eventopia/payouts/${payoutId}/reject`, { rejectionReason });
    return res.data;
}

export async function fetchPayoutHistory({ page = 1, limit = 10, currency }: { page?: number, limit?: number, currency?: string } = {}) {
    const params: any = { page, limit };
    if (currency) params.currency = currency;
    const res = await api.get('/eventopia/payouts', { params });
    return res.data;
}

export async function fetchPayoutById(payoutId: string) {
    const res = await api.get(`/eventopia/payouts/${payoutId}`);
    return res.data;
}

export async function cancelPayout(payoutId: string) {
    const res = await api.delete(`/eventopia/payouts/${payoutId}`);
    return res.data;
}

export async function retryPayout(payoutId: string) {
    const res = await api.post(`/eventopia/payouts/admin/${payoutId}/retry`);
    return res.data;
}

export async function updatePayoutAmount(payoutId: string, amount: number) {
    const res = await api.put(`/eventopia/payouts/admin/${payoutId}/amount`, { amount });
    return res.data;
}

export async function getPayoutStats(currency?: string) {
    const res = await api.get('/eventopia/payouts/stats/overview', { params: currency ? { currency } : undefined });
    return res.data;
}

export async function getEventPayoutSummary() {
    const res = await api.get('/eventopia/payouts/events/summary');
    return res.data;
}

// =====================
// Messages API
// =====================
export async function sendEmail(emailData: any) {
    const res = await api.post('/eventopia/messages/send-email', emailData);
    return res.data;
}

export async function sendOrderEmail(orderId: string, emailData: { subject: string; body: string }) {
    const res = await api.post(`/eventopia/orders/${orderId}/send-email`, emailData);
    return res.data;
}

// Resend the standard order confirmation email (uses hashed code)
export async function resendOrderConfirmation(orderId: string) {
    const res = await api.post(`/eventopia/orders/${orderId}/resend-confirmation`, {});
    return res.data;
}

export async function updateOrderInfo(orderId: string, payload: {
    name?: string;
    email?: string;
    phone?: string;
    tickets?: { index: number; attendeeName?: string; attendeeEmail?: string; attendeePhone?: string }[];
}) {
    const res = await api.patch(`/eventopia/orders/${orderId}/info`, payload);
    return res.data;
}

export async function resendOrderConfirmationEmail(hashedCode: string) {
    const res = await api.get(`/eventopia/payments/email/${hashedCode}`);
    return res.data;
}

export async function sendNotification(notificationData: any) {
    const res = await api.post('/eventopia/messages/send-notification', notificationData);
    return res.data;
}

export async function fetchMessageHistory(eventId: string) {
    const res = await api.get(`/eventopia/messages/history/${eventId}`);
    return res.data;
}

// =====================
// Search API
// =====================
export async function searchEventsByLocation({ location, radius }: { location: string, radius?: number }) {
    const params: any = { location };
    if (radius) params.radius = radius;
    const res = await api.get('/eventopia/search/events/location', { params });
    return res.data;
}

// Search events by coordinates with radius using existing events endpoint
export async function searchEventsByCoordinates({ lat, lng, radius = 10, page = 0, limit = 8, category, status, search, price, date }: {
    lat: number,
    lng: number,
    radius?: number,
    page?: number,
    limit?: number,
    category?: string,
    status?: string,
    search?: string,
    price?: string,
    date?: string
}) {
    const params: any = { lat, lng, radius, page, limit };
    if (category) params.category = category;
    if (status) params.status = status;
    if (search) params.search = search;
    if (price && price !== 'all') params.priceType = price;
    if (date) params.date = date;
    const res = await api.get('/eventopia/events', { params });
    return res.data;
}

export async function searchEventsByDateRange({ startDate, endDate }: { startDate: string, endDate: string }) {
    const params: any = { startDate, endDate };
    const res = await api.get('/eventopia/search/events/date-range', { params });
    return res.data;
}

export async function searchEventsByCategory(category: string) {
    const res = await api.get(`/eventopia/search/events/category/${category}`);
    return res.data;
}

export async function fetchPopularEvents({ limit }: { limit?: number }) {
    const params: any = {};
    if (limit) params.limit = limit;
    const res = await api.get('/eventopia/search/events/popular', { params });
    return res.data;
}

export async function fetchUpcomingEvents({ limit }: { limit?: number }) {
    const params: any = {};
    if (limit) params.limit = limit;
    const res = await api.get('/eventopia/search/events/upcoming', { params });
    return res.data;
}

export async function fetchNewEvents({ limit }: { limit?: number }) {
    const params: any = {};
    if (limit) params.limit = limit;
    const res = await api.get('/eventopia/search/events/new', { params });
    return res.data;
}

export async function fetchEventSuggestions({ limit }: { limit?: number }) {
    const params: any = {};
    if (limit) params.limit = limit;
    const res = await api.get('/eventopia/search/events/suggestions', { params });
    return res.data;
}

// =====================
// Upload API
// =====================
export async function uploadBanner(eventId: string, formData: FormData) {
    const res = await api.post(`/eventopia/upload/banner/${eventId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
}

export async function uploadSocialImage(eventId: string, formData: FormData) {
    const res = await api.post(`/eventopia/upload/social-image/${eventId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
}

export async function uploadAdditionalBanners(eventId: string, formData: FormData) {
    const res = await api.post(`/eventopia/upload/additional-banners/${eventId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
}

export async function uploadThumbnail(eventId: string, formData: FormData) {
    const res = await api.post(`/eventopia/upload/thumbnail/${eventId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
}

export async function uploadCustomImage(eventId: string, formData: FormData) {
    const res = await api.post(`/eventopia/upload/custom/${eventId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
}

export async function fetchEventAttachments(eventId: string, category?: string) {
    const params: any = {};
    if (category) params.category = category;
    const res = await api.get(`/eventopia/upload/attachments/${eventId}`, { params });
    return res.data;
}

export async function deleteEventAttachments(eventId: string, category?: string, path?: string, index?: number) {
    const params: any = {};
    if (category) params.category = category;
    if (path) params.path = path;
    else if (index != null && !isNaN(index)) params.index = index;
    const res = await api.delete(`/eventopia/upload/attachments/${eventId}`, { params });
    return res.data;
}

// =====================
// Cart API
// =====================
export async function addOrUpdateCartItem(cartData: any) {
    const res = await api.post('/eventopia/cart', cartData);
    return res.data;
}

// Auth: Bulk replace cart items (single request)
export async function addOrUpdateCartItemsBulk(cartData: { items: any[] }) {
    const res = await api.post('/eventopia/cart/bulk', cartData);
    return res.data;
}

// Guest: Add or update cart item
export async function addOrUpdateGuestCartItem(cartData: any & { guestCartId: string }) {
    const res = await api.post('/eventopia/cart/guest', cartData);
    return res.data;
}

// Guest: Bulk replace cart items (single request)
export async function addOrUpdateGuestCartItemsBulk(cartData: { guestCartId: string; items: any[] }) {
    const res = await api.post('/eventopia/cart/guest/bulk', cartData);
    return res.data;
}

export async function fetchCartItems() {
    const res = await api.get(`/eventopia/cart/`);
    return res.data;
}

// Guest: Get entire cart
export async function fetchGuestCartItems(guestCartId: string) {
    const res = await api.get(`/eventopia/cart/guest/${guestCartId}`);
    return res.data;
}

export async function fetchCartItem(eventId: string, ticketId: string) {
    const res = await api.get(`/eventopia/cart/${eventId}/${ticketId}`);
    return res.data;
}

// Guest: Get specific cart item
export async function fetchGuestCartItem(guestCartId: string, eventId: string, ticketId: string) {
    const res = await api.get(`/eventopia/cart/guest/${guestCartId}/${eventId}/${ticketId}`);
    return res.data;
}

export async function decreaseCartItem(eventId: string, ticketId: string, selectedDate: string) {
    const res = await api.patch(`/eventopia/cart/decrease/${eventId}/${ticketId}/${selectedDate}`);
    return res.data;
}

export async function increaseCartItem(eventId: string, ticketId: string, selectedDate: string) {
    const res = await api.patch(`/eventopia/cart/increase/${eventId}/${ticketId}/${selectedDate}`);
    return res.data;
}

// Guest: Decrease quantity of a cart item
export async function decreaseGuestCartItem(guestCartId: string, eventId: string, ticketId: string, selectedDate: string) {
    const res = await api.patch(`/eventopia/cart/guest/decrease/${guestCartId}/${eventId}/${ticketId}/${selectedDate}`);
    return res.data;
}

// Guest: Increase quantity of a cart item
export async function increaseGuestCartItem(guestCartId: string, eventId: string, ticketId: string, selectedDate: string) {
    const res = await api.patch(`/eventopia/cart/guest/increase/${guestCartId}/${eventId}/${ticketId}/${selectedDate}`);
    return res.data;
}

export async function removeCartItem(eventId: string, ticketId: string, selectedDate: string) {
    const res = await api.delete(`/eventopia/cart/${eventId}/${ticketId}/${selectedDate}`);
    return res.data;
}

// Guest: Remove a cart item
export async function removeGuestCartItem(guestCartId: string, eventId: string, ticketId: string, selectedDate: string) {
    const res = await api.delete(`/eventopia/cart/guest/${guestCartId}/${eventId}/${ticketId}/${selectedDate}`);
    return res.data;
}

export async function clearCart() {
    const res = await api.delete(`/eventopia/cart/clear/`);
    return res.data;
}

// Guest: Clear the entire cart
export async function clearGuestCart(guestCartId: string) {
    const res = await api.delete(`/eventopia/cart/guest/clear/${guestCartId}`);
    return res.data;
}

// Merge guest cart with user cart
export async function mergeGuestCartWithUserCart(guestCartId: string) {
    const res = await api.post(`/eventopia/cart/merge`, { guestCartId });
    return res.data;
}

// Organizer DTO type
export type CreateOrganizerDto = {
    avatar?: File | string;
    banner?: File | string;
    orgName: string;
    profileUrl?: string;
    email: string;
    phone?: string;
    address?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    facebook?: string;
};

// =====================
// Organizer API
// =====================
export async function createOrganizer(formData: FormData) {
    const res = await api.post('/eventopia/organizer', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
}

export async function updateOrganizer(formData: FormData) {
    const res = await api.put('/eventopia/organizer', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
}

// Legacy function - kept for backward compatibility, but prefer createOrganizer/updateOrganizer
export async function upsertOrganizer(formData: FormData) {
    const res = await api.post('/eventopia/organizer/upsert', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
}

export async function fetchOrganizerById(id: string) {
    const res = await api.get(`/eventopia/organizer/${id}`);
    return res.data;
}

// Lightweight public organizer directory (count + name/avatar), used for browse/search UIs
export async function fetchOrganizerDirectory() {
    const res = await api.get('/eventopia/organizer/directory');
    return res.data;
}

export async function followOrganizer(id: string) {
    const res = await api.post(`/eventopia/organizer/${id}/follow`);
    return res.data;
}

export async function unfollowOrganizer(id: string) {
    const res = await api.post(`/eventopia/organizer/${id}/unfollow`);
    return res.data;
}

export async function deleteOrganizerAvatar() {
    const res = await api.delete(`/eventopia/organizer/avatar`);
    return res.data;
}

export async function deleteOrganizerBanner() {
    const res = await api.delete(`/eventopia/organizer/banner`);
    return res.data;
}

// Authorization Email Management API
export async function addAuthorizationEmail(organizerId: string, email: string, role: 'admin' | 'eventmanager' | 'usher') {
    const res = await api.post(`/eventopia/organizer/${organizerId}/authorization-emails`, {
        email,
        role
    });
    return res.data;
}

export async function updateAuthorizationEmail(organizerId: string, email: string, role: 'admin' | 'eventmanager' | 'usher') {
    console.log('API: Updating authorization email:', { organizerId, email, role });
    try {
        const res = await api.put(`/eventopia/organizer/${organizerId}/authorization-emails`, {
            email,
            role
        });
        console.log('API: Update response:', res.data);
        return res.data;
    } catch (error) {
        console.error('API: Update error:', error);
        throw error;
    }
}

export async function removeAuthorizationEmail(organizerId: string, email: string) {
    console.log('API: Removing authorization email:', { organizerId, email });
    try {
        const res = await api.delete(`/eventopia/organizer/${organizerId}/authorization-emails`, {
            data: { email }
        });
        console.log('API: Remove response:', res.data);
        return res.data;
    } catch (error) {
        console.error('API: Remove error:', error);
        throw error;
    }
}

export async function getOrganizersByUserAccess(userEmail: string) {
    const res = await api.get(`/eventopia/organizer/user-access/${userEmail}`);
    return res.data;
}

export async function getOrganizersByUserAdminAccess(userEmail: string) {
    const res = await api.get(`/eventopia/organizer/user-admin-access/${userEmail}`);
    return res.data;
}

export async function getOrganizersByUserEditAccess(userEmail: string) {
    const res = await api.get(`/eventopia/organizer/user-edit-access/${userEmail}`);
    return res.data;
}

export async function getMyOrganizations() {
    const res = await api.get(`/eventopia/organizer/my-organizations`);
    return res.data;
}

export async function connectEventToOrganizer(organizerId: string, eventId: string) {
    const res = await api.post(`/eventopia/organizer/${organizerId}/add-event/${eventId}`);
    return res.data;
}


// Fetch user (host/organizer) profile by ID
export async function fetchUserProfileById(id: string) {
    const res = await api.get(`/user/${id}/info`);
    const backendUser = Array.isArray(res.data) ? res.data[0] : res.data;
    if (!backendUser) return null;
    // Map backend fields to UserProfile
    return {
        userId: backendUser.id || backendUser._id || '',
        fullName: (backendUser.firstName || '') + (backendUser.lastName ? ' ' + backendUser.lastName : ''),
        email: backendUser.email || '',
        profileImage: withAvatarCacheBust(backendUser.profileImage) || '',
        isCoach: backendUser.isCoach === '1' || backendUser.isCoach === 1,
        permissions: backendUser.permissions || [],
        roles: backendUser.roles || [],
        address: backendUser.address || '',
        username: backendUser.username || '',
        // Add more mappings as needed for your UserProfile type
    };
}

export const updateUserProfileAction = async (
    userId: string,
    body: FormData,
) => {
    // Do NOT set Content-Type for FormData; browser will set it with boundary
    const response = await api.put(
        `/user/${userId}`,
        body
    );
    return response.data;
};

// =====================
// User Profile API
// =====================
export async function deleteUserProfileImage() {
    const res = await api.delete(`/user/profile/image`);
    return res.data;
}

export async function getSsoUrl() {
    const res = await api.get('/lookup/sso/link');
    return res.data;
}

export async function ssoLogin({ authenticationCode, provider = 'google' }: { authenticationCode: string; provider?: 'google' | 'apple' }) {
    const res = await api.post(`/user/login/sso/${provider}/web`, { authenticationCode });
    return res;
}

// Check if user exists in Cognito
export async function checkUserExists(email: string) {
    const res = await api.post('/auth/check-user', { email });
    return res.data;
}

// =====================
// Notifications API
// =====================
export async function fetchNotifications() {
    const res = await api.get('/eventopia/notification');
    return res.data;
}

export async function markNotificationAsRead(id: string) {
    const res = await api.post(`/eventopia/notification/${id}/read`);
    return res.data;
}

// =====================
// Event Comments API
// =====================

// Create a comment
export async function createComment(eventId: string, body: string, parentCommentId?: string) {
    const commentData: any = { body };
    if (parentCommentId) commentData.parentCommentId = parentCommentId;
    const res = await api.post(`/eventopia/events/${eventId}/comments`, commentData);
    return res.data;
}

// Get all comments with nested replies
export async function fetchComments(eventId: string, page: number = 1, limit: number = 10) {
    const params: any = { page, limit };
    const res = await api.get(`/eventopia/events/${eventId}/comments`, { params });
    return res.data;
}

// Get single comment
export async function fetchCommentById(commentId: string) {
    const res = await api.get(`/eventopia/events/comments/${commentId}`);
    return res.data;
}

// Update a comment
export async function updateComment(commentId: string, body: string) {
    const res = await api.put(`/eventopia/events/comments/${commentId}`, { body });
    return res.data;
}

// Delete a comment
export async function deleteComment(commentId: string) {
    const res = await api.delete(`/eventopia/events/comments/${commentId}`);
    return res.data;
}

export async function fetchModerationComments(eventId: string, status: string = 'all', page: number = 1, limit: number = 10) {
    const res = await api.get(`/eventopia/events/${eventId}/comments/moderation`, { params: { status, page, limit } });
    return res.data;
}

export async function fetchPendingCommentCount(eventId: string) {
    const res = await api.get(`/eventopia/events/${eventId}/comments/pending-count`);
    return res.data;
}

export async function approveComment(commentId: string) {
    const res = await api.post(`/eventopia/events/comments/${commentId}/approve`);
    return res.data;
}

export async function createContactUs(body: string) {
    const commentData: any = { body };
    const res = await api.post(`/comment/anonymous/user/1`, commentData);
    return res.data;
}

// =====================
// Promo Codes API
// =====================
export async function getPromoCodes(eventId?: string, page: number = 1, limit: number = 10) {
    const params = new URLSearchParams();
    if (eventId) params.append('eventId', eventId);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const res = await api.get(`/eventopia/promo-codes?${params.toString()}`);
    return res.data;
}

export async function getPromoCodeById(id: string) {
    const res = await api.get(`/eventopia/promo-codes/${id}`);
    return res.data;
}

export async function createPromoCode(data: any) {
    const res = await api.post('/eventopia/promo-codes', data);
    return res.data;
}

export async function updatePromoCode(id: string, data: any) {
    const res = await api.put(`/eventopia/promo-codes/${id}`, data);
    return res.data;
}

export async function deletePromoCode(id: string) {
    const res = await api.delete(`/eventopia/promo-codes/${id}`);
    return res.data;
}

export async function validatePromoCode(data: any) {
    const res = await api.post('/eventopia/promo-codes/validate', data);
    return res.data;
}

export async function checkMemberPricing(data: { email: string; items: Array<{ eventId: string; ticketId: string; quantity: number }> }) {
    const res = await api.post('/eventopia/member-pricing/check', data);
    return res.data;
}

// =====================
// Pricing Members API (organization-wide member-pricing eligibility list)
// =====================
export interface PricingMember {
    _id: string;
    organizerId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    type?: string;
    createdAt: string;
    updatedAt: string;
}

export interface PricingMemberInput {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    type?: string;
}

export async function getPricingMembers(organizerId: string): Promise<PricingMember[]> {
    const res = await api.get(`/eventopia/organizers/${organizerId}/pricing-members`);
    return res.data;
}

export async function createPricingMember(organizerId: string, data: PricingMemberInput): Promise<PricingMember> {
    const res = await api.post(`/eventopia/organizers/${organizerId}/pricing-members`, data);
    return res.data;
}

export async function updatePricingMember(organizerId: string, id: string, data: Partial<PricingMemberInput>): Promise<PricingMember> {
    const res = await api.put(`/eventopia/organizers/${organizerId}/pricing-members/${id}`, data);
    return res.data;
}

export async function deletePricingMember(organizerId: string, id: string) {
    const res = await api.delete(`/eventopia/organizers/${organizerId}/pricing-members/${id}`);
    return res.data;
}

export async function bulkImportPricingMembers(organizerId: string, members: PricingMemberInput[]): Promise<{ created: number; skipped: number }> {
    const res = await api.post(`/eventopia/organizers/${organizerId}/pricing-members/bulk`, { members });
    return res.data;
}

// =====================
// Event Reports API
// =====================
export async function reportEvent(eventId: string, reason: string, description?: string) {
    const reportData = {
        eventId,
        reason,
        description
    };
    const res = await api.post('/eventopia/event-reports', reportData);
    return res.data;
}

// =====================
// Contacts API
// =====================
export async function fetchContacts({ page = 1, limit = 10, search, list, tag }: { page?: number, limit?: number, search?: string, list?: string, tag?: string } = {}) {
    const params: any = { page, limit };
    if (search) params.search = search;
    if (list && list !== 'all') params.list = list;
    if (tag && tag !== 'all') params.tag = tag;
    const res = await api.get('/eventopia/contacts', { params });
    return res.data;
}

export async function createContact(contactData: any) {
    const res = await api.post('/eventopia/contacts', contactData);
    return res.data;
}

export async function fetchContactById(id: string) {
    const res = await api.get(`/eventopia/contacts/${id}`);
    return res.data;
}

export async function updateContact(id: string, contactData: any) {
    const res = await api.put(`/eventopia/contacts/${id}`, contactData);
    return res.data;
}

export async function deleteContact(id: string) {
    const res = await api.delete(`/eventopia/contacts/${id}`);
    return res.data;
}

export async function bulkDeleteContacts(contactIds: string[]) {
    const res = await api.delete('/eventopia/contacts/bulk', { data: { contactIds } });
    return res.data;
}

export async function addContactsToList(contactIds: string[], listId: string) {
    const res = await api.post('/eventopia/contacts/add-to-list', { contactIds, listId });
    return res.data;
}

export async function copyContactsToList(contactIds: string[], listId: string) {
    const res = await api.post('/eventopia/contacts/copy-to-list', { contactIds, listId });
    return res.data;
}

export async function moveContactsToList(contactIds: string[], listId: string) {
    const res = await api.post('/eventopia/contacts/move-to-list', { contactIds, listId });
    return res.data;
}

export async function removeContactsFromList(contactIds: string[], listId: string) {
    const res = await api.post('/eventopia/contacts/remove-from-list', { contactIds, listId });
    return res.data;
}

export async function createListAndAddContacts(listData: any, contactIds: string[]) {
    const res = await api.post('/eventopia/contacts/create-list-and-add', { listData, contactIds });
    return res.data;
}

export async function sendEmailToList(
    listId: string,
    subject: string,
    message: string,
    messageFile?: File,
    emailName?: string,
    replyToEmail?: string
) {
    if (messageFile) {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('listId', listId);
        formData.append('subject', subject);
        formData.append('messageFile', messageFile);
        if (emailName) {
            formData.append('emailName', emailName);
        }
        if (replyToEmail) {
            formData.append('replyToEmail', replyToEmail);
        }

        const res = await api.post('/eventopia/contacts/send-email', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return res.data;
    } else {
        // Use existing API for text messages
        const payload: any = { listId, subject, message };
        if (emailName) {
            payload.emailName = emailName;
        }
        if (replyToEmail) {
            payload.replyToEmail = replyToEmail;
        }
        const res = await api.post('/eventopia/contacts/send-email', payload);
        return res.data;
    }
}

export async function sendNotificationToList(listId: string, title: string, message: string) {
    const res = await api.post('/eventopia/contacts/send-notification', { listId, title, message });
    return res.data;
}

export async function getEmailRequestsByListId(listId: string, status?: string) {
    const params = status ? { status } : {};
    const res = await api.get(`/eventopia/contacts/email-requests/${listId}`, { params });
    return res.data;
}

export async function getEmailRecipients(requestId: string, status?: string) {
    const params = status ? { status } : {};
    const res = await api.get(`/eventopia/contacts/email-requests/${requestId}/recipients`, { params });
    return res.data;
}

export async function retryFailedEmails(requestId: string) {
    const res = await api.post(`/eventopia/contacts/email-requests/${requestId}/retry-failed`);
    return res.data;
}

// Get active email jobs for organization with progress
export async function getActiveEmailJobs(organizationId: string) {
    const res = await api.get(`/eventopia/contacts/email-jobs/active`, {
        params: { organizationId }
    });
    return res.data;
}

// Get email job status with progress
export async function getEmailJobStatus(requestId: string) {
    const res = await api.get(`/eventopia/contacts/email-jobs/${requestId}`);
    return res.data;
}

export async function bulkImportContactsFromCSV(formData: FormData) {
    const res = await api.post('/eventopia/contacts/bulk-import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
}

// =====================
// Contact Lists API
// =====================
export async function fetchContactLists({ page = 1, limit = 10 }: { page?: number, limit?: number } = {}) {
    const params: any = { page, limit };
    const res = await api.get('/eventopia/contacts/lists', { params });
    return res.data;
}

export async function createContactList(listData: any) {
    const res = await api.post('/eventopia/contacts/lists', listData);
    return res.data;
}

export async function fetchContactListById(id: string) {
    const res = await api.get(`/eventopia/contacts/lists/${id}`);
    return res.data;
}

export async function updateContactList(id: string, listData: any) {
    const res = await api.put(`/eventopia/contacts/lists/${id}`, listData);
    return res.data;
}

export async function deleteContactList(id: string) {
    const res = await api.delete(`/eventopia/contacts/lists/${id}`);
    return res.data;
}

// Contact List Form APIs
export async function updateContactListForm(id: string, formData: any) {
    const res = await api.put(`/eventopia/contacts/lists/${id}/form`, formData);
    return res.data;
}

export async function getContactListById(id: string) {
    const res = await api.get(`/eventopia/public/contact-lists/${id}`);
    return res.data;
}

export async function submitContactListForm(id: string, answers: any, submitterName: string, submitterEmail: string, submitterPhone?: string) {
    const res = await api.post(`/eventopia/public/contact-lists/${id}/submit`, {
        answers,
        submitterName,
        submitterEmail,
        submitterPhone
    });
    return res.data;
}

export async function getContactListSubmissions(id: string, page: number = 1, limit: number = 50) {
    const params = { page, limit };
    const res = await api.get(`/eventopia/contacts/lists/${id}/submissions`, { params });
    return res.data;
}

export async function fetchAllTags() {
    const res = await api.get('/eventopia/contacts/tags/all');
    return res.data;
}

export async function fetchAllLists() {
    const res = await api.get('/eventopia/contacts/lists/all');
    return res.data;
}

export async function uploadEmailImage(imageFile: File) {
    const formData = new FormData();
    formData.append('image', imageFile);

    const res = await api.post('/eventopia/contacts/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
}

// =====================
// Apple Wallet API
// =====================

/**
 * Downloads an Apple Wallet pass for a ticket by order ID and ticket index
 * @param orderId - The order ID
 * @param ticketIndex - The ticket index in the order
 * @returns Promise that resolves with the pass blob
 */
export async function downloadAppleWalletPassByOrder(orderId: string, ticketIndex: number): Promise<Blob> {
    const res = await api.get(`/eventopia/apple-wallet/order/${orderId}/ticket/${ticketIndex}`, {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.apple.pkpass',
        },
    });
    return res.data;
}

/**
 * Downloads an Apple Wallet pass for a ticket by hashed ID and QR code
 * @param hashedId - The hashed unique ID of the order
 * @param qrCode - The QR code of the ticket
 * @returns Promise that resolves with the pass blob
 */
export async function downloadAppleWalletPassByHash(hashedId: string, qrCode: string): Promise<Blob> {
    const res = await api.get(`/eventopia/apple-wallet/hash/${hashedId}/qr/${qrCode}`, {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.apple.pkpass',
        },
    });
    return res.data;
}

/**
 * Downloads an Apple Wallet pass for a contact list QR code
 * @param contactListId - The ID of the contact list
 * @param organizerId - Optional organizer ID for organization logo
 * @returns Promise that resolves with the pass blob
 */
export async function downloadContactListAppleWalletPass(contactListId: string, organizerId?: string): Promise<Blob> {
    const params = organizerId ? { organizerId } : {};
    const res = await api.get(`/eventopia/apple-wallet/contact-list/${contactListId}`, {
        params,
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.apple.pkpass',
        },
    });
    return res.data;
}

/**
 * Downloads a Google Wallet pass for a contact list QR code
 * @param contactListId - The ID of the contact list
 * @param organizerId - Optional organizer ID for organization logo
 * @returns Promise that resolves with the pass URL or blob
 */
export async function downloadContactListGoogleWalletPass(contactListId: string, organizerId?: string): Promise<string> {
    const params = organizerId ? { organizerId } : {};
    const res = await api.get(`/eventopia/google-wallet/contact-list/${contactListId}`, {
        params,
    });
    return res.data.saveUrl || res.data.url;
}


export async function fetchTierSummary(eventId: string) {
    const res = await api.get(`/eventopia/tickets/events/${eventId}/tier-summary`);
    return res.data?.data;
}
